import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { asyncScheduler, EMPTY as empty, Observable, of, observable } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
	SitesActionTypes,
	GetSites,
	GetSitesFailure,
	GetSitesSuccess,
	GetSite,
	GetSiteFailure,
	GetSiteSuccess,
	UpdateSite,
	UpdateSiteSuccess,
	UpdateSiteFailure,
	AddSite,
	AddSiteSuccess,
	AddSiteFailure,
	RemoveSite,
	RemoveSiteSuccess,
	RemoveSiteFailure,
	AddFacilityHierarchyLevel,
	AddFacilityHierarchyLevelSuccess,
	AddFacilityHierarchyLevelFailure,
	UpdateFacilityHierarchyLevel,
	UpdateFacilityHierarchyLevelSuccess,
	UpdateFacilityHierarchyLevelFailure,
	DeleteFacilityHierarchyLevel,
	DeleteFacilityHierarchyLevelSuccess,
	DeleteFacilityHierarchyLevelFailure,
	AddFacilityHierarchyLevelName,
	AddFacilityHierarchyLevelNameSuccess,
	UpdateFacilityHierarchyLevelName,
	UpdateFacilityHierarchyLevelNameSuccess,
	DeleteFacilityHierarchyLevelName,
	DeleteFacilityHierarchyLevelNameSuccess,
	DeleteFacilityHierarchyLevelNameFailure,
	GetFacilityHierarchyLevelNamesForSite,
	GetFacilityHierarchyLevelNamesForSiteSuccess,
	GetFacilityHierarchyLevelNamesForSiteFailure,
} from '../actions/sites.actions';
import { SitesService } from '../../sites.service';
import { Site, Point } from '../../sites';
import { ToastService } from '../../../toast/toast.service';

@Injectable()
export class SitesEffects {
	getSites$ = createEffect(() =>
    this.actions$.pipe(
		ofType<GetSites>(SitesActionTypes.GetSites),
		switchMap(() => {
			return this.sitesService.getSites().pipe(
				switchMap(sites => {
					
					return of(new GetSitesSuccess(sites));
				}),
				catchError(error => of(new GetSitesFailure(error)))
			);
		})
		)
	);

	getSite$ = createEffect(() =>
    this.actions$.pipe(
		ofType<GetSite>(SitesActionTypes.GetSite),
		map(action => action.siteId),
		switchMap(siteId => {
			return this.sitesService.getSite(siteId).pipe(
				switchMap(site => {
					return of(new GetSiteSuccess(site));
				}),
				catchError(error => of(new GetSiteFailure(error)))
			);
		})
	)
	);

	updateSite$ = createEffect(() =>
    this.actions$.pipe(
		ofType<UpdateSite>(SitesActionTypes.UpdateSite),
		map(action => action.payload),
		switchMap(site => {
			return this.sitesService.updateSite(site).pipe(
				switchMap(siteData => {
					return of(new UpdateSiteSuccess(siteData));
				}),
				catchError(error => of(new UpdateSiteFailure(error)))
			);
		})
	)
	);

	addSite$ = createEffect(() =>
    this.actions$.pipe(
		ofType<AddSite>(SitesActionTypes.AddSite),
		map(action => action.payload),
		switchMap(site => {
			return this.sitesService.addSite(site).pipe(
				switchMap(siteData => {
					return of(new AddSiteSuccess(siteData));
				}),
				catchError(error => of(new AddSiteFailure(error)))
			);
		})
	)
	);

	removeSite$ = createEffect(() =>
    this.actions$.pipe(
		ofType<RemoveSite>(SitesActionTypes.RemoveSite),
		map(action => action.payload),
		switchMap(site => {
			return this.sitesService.removeSite(site).pipe(
				switchMap(siteData => {
					this.toastService.addMessage({ text: `Site "${site.name}" was successfully deleted.`, error: false });
					return of(new RemoveSiteSuccess(siteData));
				}),
				catchError(error => {
					this.toastService.addMessage({ text: `An error occured while deleting the site.`, error: true });
					return of(new RemoveSiteFailure(error));
				})
			);
		})
	)
	);

	addFacilityHierarchyLevel$ = createEffect(() =>
    this.actions$.pipe(
		ofType<AddFacilityHierarchyLevel>(SitesActionTypes.AddFacilityHierarchyLevel),
		map(action => action.newFacilityHierarchyLevel),
		switchMap(level => {
			return this.sitesService.addFacilityHierarchyLevel(level).pipe(
				switchMap(newLevel => {
					return of(new AddFacilityHierarchyLevelSuccess(newLevel));
				}),
				catchError(error => of(new AddSiteFailure(error)))
			);
		})
	)
	);

	updateFacilityHierarchyLevel$ = createEffect(() =>
    this.actions$.pipe(
		ofType<UpdateFacilityHierarchyLevel>(SitesActionTypes.UpdateFacilityHierarchyLevel),
		map(action => action.editFacilityHierachyLevel),
		switchMap(level => {
			return this.sitesService.updateFacilityHierarchyLevel(level).pipe(
				switchMap(updatedLevel => {
					return of(new UpdateFacilityHierarchyLevelSuccess(updatedLevel));
				}),
				catchError(error => of(new UpdateSiteFailure(error)))
			);
		})
	)
	);

	deleteFacilityHierarchyLevel$ = createEffect(() =>
    this.actions$.pipe(
		ofType<DeleteFacilityHierarchyLevel>(SitesActionTypes.DeleteFacilityHierarchyLevel),
		map(action => action.levelId),
		switchMap(levelId => {
			return this.sitesService.deleteFacilityHierarchyLevel(levelId).pipe(
				switchMap(() => {
					return of(new DeleteFacilityHierarchyLevelSuccess());
				}),
				catchError(error => of(new DeleteFacilityHierarchyLevelFailure(error)))
			);
		})
	)
	);

	addFacilityHierarchyLevelName$ = createEffect(() =>
    this.actions$.pipe(
		ofType<AddFacilityHierarchyLevelName>(SitesActionTypes.AddFacilityHierarchyLevelName),
		map(action => action.newFacilityHierachyLevelName),
		switchMap(levelName => {
			return this.sitesService.addFacilityHierarchyLevelName(levelName).pipe(
				switchMap(newLevelName => {
					return of(new AddFacilityHierarchyLevelNameSuccess(newLevelName));
				}),
				catchError(error => of(new AddSiteFailure(error)))
			);
		})
	)
	);

	updateFacilityHierarchyLevelName$ = createEffect(() =>
    this.actions$.pipe(
		ofType<UpdateFacilityHierarchyLevelName>(SitesActionTypes.UpdateFacilityHierarchyLevelName),
		map(action => action.editFacilityHierachyLevelName),
		switchMap(levelName => {
			return this.sitesService.updateFacilityHierarchyLevelName(levelName).pipe(
				switchMap(updatedLevelName => {
					return of(new UpdateFacilityHierarchyLevelNameSuccess(updatedLevelName));
				}),
				catchError(error => of(new UpdateSiteFailure(error)))
			);
		})
	)
	);

	deleteFacilityHierarchyLevelName$ = createEffect(() =>
    this.actions$.pipe(
		ofType<DeleteFacilityHierarchyLevelName>(SitesActionTypes.DeleteFacilityHierarchyLevelName),
		map(action => action.levelNameId),
		switchMap(levelNameId => {
			return this.sitesService.deleteFacilityHierarchyLevelName(levelNameId).pipe(
				switchMap(() => {
					return of(new DeleteFacilityHierarchyLevelNameSuccess());
				}),
				catchError(error => of(new DeleteFacilityHierarchyLevelNameFailure(error)))
			);
		})
	)
	);

	getFacilityHierarchyLevelNamesForSite$ = createEffect(() =>
    this.actions$.pipe(
		ofType<GetFacilityHierarchyLevelNamesForSite>(SitesActionTypes.GetFacilityHierarchyLevelNamesForSite),
		map(action => action.siteId),
		switchMap(siteId => {
			return this.sitesService.getFacilityHierarchyLevelNamesForSite(siteId).pipe(
				switchMap(topLevelName => {
					return of(new GetFacilityHierarchyLevelNamesForSiteSuccess(topLevelName));
				}),
				catchError(error => of(new GetFacilityHierarchyLevelNamesForSiteFailure(error)))
			);
		})
	)
	);

	constructor(private actions$: Actions, private sitesService: SitesService, public toastService: ToastService) {}
}
